import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import { useToast } from "../../components/ToastContainer";
import { useNotifications } from "../../context/NotificationContext";
import LiveStreamViewer from "../../components/LiveStreamViewer";
import axios from "axios";
import "./style.css";

// Utility function to format relative time
const formatRelativeTime = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }
  
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
  }
  
  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
};

export default function NotificationsPage() {
  const { user } = useSelector((state) => ({ ...state }));
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("newest"); // newest, oldest
  const [filterType, setFilterType] = useState("all"); // all, like, comment, comment_reply, friend_request
  const [selectedLiveStream, setSelectedLiveStream] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  
  // Use real-time notifications from context
  const { 
    notifications, 
    markAsRead: markAsReadContext, 
    removeNotification, 
    clearAllNotifications: clearNotificationsContext,
    updateNotification,
    refetchNotifications: refetchNotificationsFromContext
  } = useNotifications();

  // Remove the old fetchNotifications effect since we're using real-time data
  useEffect(() => {
    setLoading(false); // Set loading to false since notifications come from context
  }, []);

  useEffect(() => {
    filterAndSortNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, searchQuery, sortOrder, filterType]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const liveStreamId = params.get("liveStreamId");

    if (liveStreamId) {
      setSelectedLiveStream(liveStreamId);
      navigate('/notifications', { replace: true });
    }
  }, [location.search, navigate]);

  // Remove fetchNotifications since we're using real-time data from context

  const filterAndSortNotifications = () => {
    let filtered = [...notifications];

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(notif => 
        notif.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (notif.from?.first_name + " " + notif.from?.last_name).toLowerCase().includes(searchQuery.toLowerCase()) ||
        (notif.commentContent && notif.commentContent.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by type
    if (filterType !== "all") {
      filtered = filtered.filter(notif => notif.type === filterType);
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredNotifications(filtered);
  };

  const handleNotificationClick = (notif) => {
    if (notif.type === "friend_request") {
      navigate("/friends");
    } else if (notif.type === "comment" || notif.type === "like" || notif.type === "comment_reply" || notif.type === "friend_tag" || notif.type === "post" || notif.type === "group_post") {
      if (notif.postId) {
        navigate(`/post/${notif.postId}`);
      }
    } else if (notif.type === "live_stream") {
      if (notif.liveStreamId) {
        // Open live stream viewer
        setSelectedLiveStream(notif.liveStreamId);
      }
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`/notification/${notificationId}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      removeNotification(notificationId); // Use context method
    } catch (error) {
      console.error("Error deleting notification:", error);
      // Silently handle errors or show a subtle notification instead of alert
    }
  };

  const clearAllNotifications = async () => {
    if (window.confirm("Are you sure you want to clear all notifications? This action cannot be undone.")) {
      try {
        await axios.delete("/notifications/clear", {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        clearNotificationsContext(); // Use context method
      } catch (error) {
        console.error("Error clearing notifications:", error);
        alert("Failed to clear notifications");
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/notification/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      markAsReadContext(notificationId); // Use context method
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Handle friend request actions
  const handleFriendRequestAction = async (action, notificationId, senderId, senderName) => {
    try {
      // Ensure both IDs are strings
      const notificationIdStr = notificationId ? (typeof notificationId === 'string' ? notificationId : notificationId.toString()) : null;
      const senderIdStr = senderId ? (typeof senderId === 'string' ? senderId : senderId.toString()) : null;
      
      if (!notificationIdStr || !senderIdStr) {
        showError("❌ Unable to process: Invalid notification or sender information", 4000);
        return;
      }
      
      const endpoint = action === "accept" ? "/notification/acceptFriend" : "/notification/declineFriend";
      await axios.put(endpoint, {
        notificationId: notificationIdStr,
        senderId: senderIdStr
      }, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      // Optimistically update the notification for immediate UI feedback
      // The server will also emit a Socket.IO event, but this ensures UI updates even if Socket.IO is delayed
      const status = action === "accept" ? "accepted" : "declined";
      updateNotification(notificationIdStr, { friendRequestStatus: status });
      
      // Show beautiful toast notification
      if (action === "accept") {
        showSuccess(`✅ You are now friends with ${senderName}!`, 4000);
      } else {
        showError(`❌ Friend request from ${senderName} declined`, 3000);
      }
      
    } catch (error) {
      console.error("Error handling friend request:", error);
      const errorMessage = error.response?.data?.message || "Failed to process friend request. Please try again.";
      showError(`❌ ${errorMessage}`, 4000);
    }
  };

  if (loading) {
    return (
      <>
        <Header 
          showMessengerSidebar={false}
          onMessengerToggle={() => {}}
        />
        <div className="notifications_page">
          <div className="notifications_container">
            <div className="loading">Loading notifications...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="notifications_page">
        <div className="notifications_container">
          <div className="notifications_header">
            <div className="header_left">
              <button 
                className="back_btn"
                onClick={() => {
                  if (window.history.length > 1) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
                aria-label="Go back"
              >
                ← Back
              </button>
              <h1>Notifications</h1>
            </div>
            <div className="header_actions">
              <button 
                className="clear_all_btn"
                onClick={clearAllNotifications}
                disabled={notifications.length === 0}
              >
                🗑️ Clear All
              </button>
            </div>
          </div>

          <div className="notifications_controls">
            <div className="search_container">
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search_input"
              />
            </div>

            <div className="filters_container">
              <select 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
                className="filter_select"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>

              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                className="filter_select"
              >
                <option value="all">All Types</option>
                <option value="like">Likes</option>
                <option value="comment">Comments</option>
                <option value="comment_reply">Replies</option>
                <option value="friend_request">Friend Requests</option>
                <option value="live_stream">Live Streams</option>
              </select>
            </div>
          </div>

          <div className="notifications_stats">
            <span>Showing {filteredNotifications.length} of {notifications.length} notifications</span>
            <span>{notifications.filter(n => !n.isRead).length} unread</span>
          </div>

          <div className="notifications_list">
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map((notif) => (
                <div
                  key={notif._id}
                  className={`notification_item ${!notif.isRead ? 'unread' : ''}`}
                >
                  <div 
                    className="notification_content"
                    onClick={() => notif.type !== "friend_request" && handleNotificationClick(notif)}
                    style={{
                      cursor: notif.type !== "friend_request" ? "pointer" : "default",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 15,
                      flex: 1
                    }}
                  >
                    <img
                      src={notif.from?.picture || "/images/default_pic.png"}
                      alt="profile"
                      className="notification_avatar"
                    />
                    <div className="notification_details" style={{ flex: 1 }}>
                      <div className="notification_message">
                        {notif.type === "unblock_request_accepted" && (
                          <span style={{ color: 'green', fontWeight: 500 }}>
                            {notif.message || `Your unblock request has been accepted by ${notif.from?.name || notif.from?.first_name || 'a user'}.`}
                          </span>
                        )}
                        {notif.type === "unblock_request_declined" && (
                          <span style={{ color: 'red', fontWeight: 500 }}>
                            {notif.message || "Your unblock request was declined."}
                          </span>
                        )}
                        {/* Show Request Unblock button if this is a block notification */}
                        {notif.type === "other" && notif.message && notif.message.toLowerCase().includes("blocked") ? (
                          <>
                            <span>{notif.message}</span>
                            <button
                              style={{ marginLeft: 10, padding: '2px 8px', borderRadius: 4, border: '1px solid #007bff', background: '#eaf3ff', color: '#007bff', cursor: 'pointer', fontSize: 13 }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await axios.post("/api/block/sendUnblockRequest", { blockerId: notif.from?._id }, {
                                    headers: { Authorization: `Bearer ${user.token}` },
                                  });
                                  showSuccess("Unblock request sent!");
                                } catch (err) {
                                  showError("Failed to send unblock request.");
                                }
                              }}
                            >Request Unblock</button>
                          </>
                        ) : null}
                        {notif.type !== "unblock_request_accepted" && notif.type !== "unblock_request_declined" && !(notif.type === "other" && notif.message && notif.message.toLowerCase().includes("blocked")) && notif.message}
                      </div>
                      
                      {/* Show original comment context for reply notifications */}
                      {notif.type === "comment_reply" && notif.originalCommentContent && (
                        <div className="original_comment_context">
                          <div className="context_label">Original comment:</div>
                          <div className="original_comment_text">"{notif.originalCommentContent}"</div>
                        </div>
                      )}
                      
                      <div className="notification_meta">
                        <span className="notification_user">
                          {notif.from?.first_name} {notif.from?.last_name}
                        </span>
                        {notif.type === "like" && notif.reactionType && (
                          <span className="reaction_emoji">
                            {notif.reactionType === "like" && "👍"}
                            {notif.reactionType === "love" && "❤️"}
                            {notif.reactionType === "haha" && "😂"}
                            {notif.reactionType === "wow" && "😮"}
                            {notif.reactionType === "sad" && "😢"}
                            {notif.reactionType === "angry" && "😡"}
                          </span>
                        )}
                        <span className="notification_time" title={`${new Date(notif.createdAt).toLocaleDateString()} at ${new Date(notif.createdAt).toLocaleTimeString()}`}>
                          {formatRelativeTime(notif.createdAt)}
                        </span>
                      </div>
                      {notif.postText && (notif.type === "comment" || notif.type === "like" || notif.type === "comment_reply") && (
                        <div className="notification_post_text">
                          Post: {notif.postText}
                        </div>
                      )}
                      {/* Unblock request action buttons for blocker, or status if handled (frontend workaround) */}
                      {notif.type === "unblock_request" && (() => {
                        // Check if a related unblock_request_accepted or unblock_request_declined notification exists for the same user
                        const relatedStatus = notifications.find(n =>
                          (n.type === "unblock_request_accepted" || n.type === "unblock_request_declined") &&
                          n.from?._id === notif.from?._id
                        );
                        const refetchNotifications = async () => {
                          // Refetch from API via context (no full page reload)
                          if (refetchNotificationsFromContext) {
                            await refetchNotificationsFromContext();
                          }
                        };
                        if (notif.unblockRequestStatus === "accepted" || (relatedStatus && relatedStatus.type === "unblock_request_accepted")) {
                          return <div className="friend_request_status accepted">✓ Request accepted</div>;
                        } else if (notif.unblockRequestStatus === "declined" || (relatedStatus && relatedStatus.type === "unblock_request_declined")) {
                          return <div className="friend_request_status declined" style={{ color: 'red', fontWeight: 500 }}>Request declined</div>;
                        } else {
                          return (
                            <div className="friend_request_actions">
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post("/api/block/handleUnblockRequest", {
                                      requesterId: notif.from?._id,
                                      action: "accept"
                                    }, {
                                      headers: { Authorization: `Bearer ${user.token}` }
                                    });
                                    showSuccess("User has been unblocked.");
                                    await refetchNotifications();
                                  } catch (err) {
                                    showError("Failed to accept unblock request.");
                                  }
                                }}
                                className="accept_btn"
                              >
                                Accept
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post("/api/block/handleUnblockRequest", {
                                      requesterId: notif.from?._id,
                                      action: "decline"
                                    }, {
                                      headers: { Authorization: `Bearer ${user.token}` }
                                    });
                                    showSuccess("Unblock request declined.");
                                    await refetchNotifications();
                                  } catch (err) {
                                    showError("Failed to decline unblock request.");
                                  }
                                }}
                                className="decline_btn"
                              >
                                Decline
                              </button>
                            </div>
                          );
                        }
                      })()}
                      {/* Friend request action buttons */}
                      {notif.type === "friend_request" && (notif.friendRequestStatus === "pending" || !notif.friendRequestStatus) && (
                        <div className="friend_request_actions">
                          <button
                            onClick={() => {
                              // Ensure senderId is always a string
                              const senderIdRaw = notif.from?._id || notif.from;
                              const senderId = senderIdRaw ? (typeof senderIdRaw === 'string' ? senderIdRaw : senderIdRaw.toString()) : null;
                              const senderName = notif.from?.first_name && notif.from?.last_name 
                                ? `${notif.from.first_name} ${notif.from.last_name}` 
                                : 'User';
                              if (!senderId) {
                                showError("❌ Unable to process: Invalid sender information", 4000);
                                return;
                              }
                              handleFriendRequestAction("accept", notif._id, senderId, senderName);
                            }}
                            className="accept_btn"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => {
                              // Ensure senderId is always a string
                              const senderIdRaw = notif.from?._id || notif.from;
                              const senderId = senderIdRaw ? (typeof senderIdRaw === 'string' ? senderIdRaw : senderIdRaw.toString()) : null;
                              const senderName = notif.from?.first_name && notif.from?.last_name 
                                ? `${notif.from.first_name} ${notif.from.last_name}` 
                                : 'User';
                              if (!senderId) {
                                showError("❌ Unable to process: Invalid sender information", 4000);
                                return;
                              }
                              handleFriendRequestAction("decline", notif._id, senderId, senderName);
                            }}
                            className="decline_btn"
                          >
                            Decline
                          </button>
                        </div>
                      )}
                      {notif.type === "friend_request" && notif.friendRequestStatus === "accepted" && (
                        <div className="friend_request_status accepted">
                          ✓ Request accepted
                        </div>
                      )}
                      {notif.type === "friend_request" && notif.friendRequestStatus === "declined" && (
                        <div className="friend_request_status declined">
                          Request declined
                        </div>
                      )}
                    </div>
                    {/* Post image next to text */}
                    {(notif.type === "comment" || notif.type === "like" || notif.type === "comment_reply") && notif.postImage && (
                      notif.postMediaType === "video" ? (
                        <video
                          src={notif.postImage}
                          className="notification_post_video"
                          onClick={() => handleNotificationClick(notif)}
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={notif.postImage}
                          alt="post"
                          className="notification_post_image"
                          onClick={() => handleNotificationClick(notif)}
                        />
                      )
                    )}
                  </div>
                  
                  <div className="notification_actions">
                    {!notif.isRead && (
                      <button
                        className="mark_read_btn"
                        onClick={() => markAsRead(notif._id)}
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="delete_btn"
                      onClick={() => deleteNotification(notif._id)}
                      title="Delete notification"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no_notifications">
                {searchQuery || filterType !== "all" ? (
                  <div>
                    <h3>No matching notifications</h3>
                    <p>Try adjusting your search or filter settings</p>
                  </div>
                ) : (
                  <div>
                    <h3>No notifications yet</h3>
                    <p>When you get likes, comments, or friend requests, they'll appear here</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Live Stream Viewer Modal */}
      {selectedLiveStream && (
        <LiveStreamViewer
          streamId={selectedLiveStream}
          onClose={() => setSelectedLiveStream(null)}
        />
      )}
    </>
  );
} 